import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  Button,
  Card,
  Form,
  Header,
  Icon,
  Input,
  Message,
  Segment,
  Step,
  Label,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';

const VisualValidate = () => {
  // --- state ---
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('Doubao-Seedance-2.0');
  const [callbackURL, setCallbackURL] = useState('');
  const [projectName, setProjectName] = useState('default');
  const [loading, setLoading] = useState(false);

  // auto-fill callbackURL on mount
  useEffect(() => {
    setCallbackURL(`${window.location.origin}/visual-validate/callback`);
  }, []);

  // step 1 result
  const [h5Link, setH5Link] = useState('');
  const [bytedToken, setBytedToken] = useState('');

  // step 2
  const [polling, setPolling] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // --- Step 1: create session ---
  const createSession = async () => {
    if (!apiKey) return showError('请填写 API Key');
    if (!callbackURL) return showError('请填写 Callback URL');
    setLoading(true);
    setH5Link('');
    setBytedToken('');
    setGroupId('');
    try {
      const res = await API.post(
        '/v1/visual_validate/session',
        {
          model,
          callback_url: callbackURL,
          project_name: projectName || 'default',
        },
        { headers: { Authorization: `Bearer ${apiKey}` } }
      );
      const data = res.data;
      const result = data?.Result || data;
      if (result?.H5Link) {
        setH5Link(result.H5Link);
        setBytedToken(result.BytedToken || '');
        showSuccess('认证链接已生成，请在 120 秒内完成认证');
        startCountdown(120);
      } else if (data?.error) {
        showError(data.error.message || JSON.stringify(data.error));
      } else {
        showError('未获取到 H5Link，请检查渠道配置');
      }
    } catch (e) {
      showError(e.message || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = (seconds) => {
    setCountdown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // --- Step 2: get result ---
  const getResult = async () => {
    if (!bytedToken) return showError('缺少 BytedToken');
    setPolling(true);
    setGroupId('');
    try {
      const res = await API.post(
        '/v1/visual_validate/result',
        {
          model,
          byted_token: bytedToken,
          project_name: projectName || 'default',
        },
        { headers: { Authorization: `Bearer ${apiKey}` } }
      );
      const data = res.data;
      const result = data?.Result || data;
      if (result?.GroupId) {
        setGroupId(result.GroupId);
        showSuccess('认证成功！');
      } else if (data?.error) {
        showError(data.error.message || JSON.stringify(data.error));
      } else {
        showError('未获取到 GroupId，认证可能尚未完成');
      }
    } catch (e) {
      showError(e.message || '请求失败');
    } finally {
      setPolling(false);
    }
  };

  // --- accordion state ---
  const [guideOpen, setGuideOpen] = useState(false);

  // --- current step ---
  const currentStep = groupId ? 3 : h5Link ? 2 : 1;

  const baseURL = window.location.origin;

  const codeStyle = {
    background: '#1b1c1d',
    color: '#a9b7c6',
    padding: '14px 16px',
    borderRadius: 6,
    fontSize: 13,
    lineHeight: 1.6,
    overflowX: 'auto',
    whiteSpace: 'pre',
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
  };

  return (
    <div className='dashboard-container'>
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Header as='h3'>
              <Icon name='user circle' />
              <Header.Content>真人认证（Visual Validate）</Header.Content>
            </Header>
          </Card.Header>
        </Card.Content>
        <Card.Content>
          {/* Progress steps */}
          <Step.Group size='small' fluid>
            <Step active={currentStep === 1} completed={currentStep > 1}>
              <Icon name='linkify' />
              <Step.Content>
                <Step.Title>生成认证链接</Step.Title>
              </Step.Content>
            </Step>
            <Step active={currentStep === 2} completed={currentStep > 2}>
              <Icon name='camera' />
              <Step.Content>
                <Step.Title>完成活体认证</Step.Title>
              </Step.Content>
            </Step>
            <Step active={currentStep === 3}>
              <Icon name='check circle' />
              <Step.Content>
                <Step.Title>获取结果</Step.Title>
              </Step.Content>
            </Step>
          </Step.Group>

          {/* Config form */}
          <Segment>
            <Form>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label>API Key</label>
                  <Input
                    placeholder='sk-...'
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    type='password'
                  />
                </Form.Field>
                <Form.Field>
                  <label>Model</label>
                  <Input
                    placeholder='Doubao-Seedance-2.0'
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label>Callback URL</label>
                  <Input
                    placeholder='https://your-domain.com/callback'
                    value={callbackURL}
                    onChange={(e) => setCallbackURL(e.target.value)}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Project Name</label>
                  <Input
                    placeholder='default'
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </Form.Field>
              </Form.Group>
              <Button
                primary
                loading={loading}
                onClick={createSession}
                disabled={loading || !apiKey || !callbackURL}
              >
                <Icon name='play' />
                生成认证链接
              </Button>
            </Form>
          </Segment>

          {/* H5 Link result */}
          {h5Link && (
            <Segment>
              <Message info>
                <Message.Header>
                  认证链接已生成
                  {countdown > 0 && (
                    <Label color='orange' style={{ marginLeft: 10 }}>
                      剩余 {countdown}s
                    </Label>
                  )}
                </Message.Header>
                <p>请在手机浏览器中打开以下链接完成活体认证（120 秒有效）：</p>
              </Message>
              <Input
                fluid
                action={{
                  icon: 'copy',
                  content: '复制',
                  onClick: () => {
                    navigator.clipboard.writeText(h5Link);
                    showSuccess('已复制到剪贴板');
                  },
                }}
                value={h5Link}
                readOnly
                style={{ marginBottom: 10 }}
              />
              <Button
                color='teal'
                onClick={() => window.open(h5Link, '_blank')}
              >
                <Icon name='external' />
                在新窗口打开
              </Button>

              <div style={{ marginTop: 20 }}>
                <Header as='h5'>BytedToken</Header>
                <Input fluid value={bytedToken} readOnly />
              </div>

              <div style={{ marginTop: 20 }}>
                <Button
                  color='green'
                  loading={polling}
                  onClick={getResult}
                  disabled={polling || !bytedToken}
                >
                  <Icon name='search' />
                  查询认证结果
                </Button>
              </div>
            </Segment>
          )}

          {/* Result */}
          {groupId && (
            <Message positive>
              <Message.Header>认证成功</Message.Header>
              <p>
                Asset Group ID: <strong>{groupId}</strong>
              </p>
              <p>
                可使用此 GroupId
                上传素材（CreateAsset），素材将自动与认证人脸比对。
              </p>
            </Message>
          )}
        </Card.Content>
      </Card>

      {/* API Guide */}
      <Card fluid style={{ marginTop: 20 }}>
        <Card.Content>
          <Accordion>
            <Accordion.Title
              active={guideOpen}
              onClick={() => setGuideOpen(!guideOpen)}
              style={{ fontSize: 16, fontWeight: 'bold' }}
            >
              <Icon name='dropdown' />
              API 调用指南 — 真人认证完整流程
            </Accordion.Title>
            <Accordion.Content active={guideOpen}>
              {/* Overview */}
              <Message info>
                <Message.Header>流程概览</Message.Header>
                <Message.List>
                  <Message.Item>
                    Step 1: 调用 <code>CreateVisualValidateSession</code> 生成
                    H5 认证链接
                  </Message.Item>
                  <Message.Item>
                    Step 2: 用户在手机/浏览器中打开 H5Link 完成活体认证（120
                    秒有效）
                  </Message.Item>
                  <Message.Item>
                    Step 3: 认证完成后，CallbackURL 会收到{' '}
                    <code>resultCode=10000</code> 和 <code>bytedToken</code>{' '}
                    参数
                  </Message.Item>
                  <Message.Item>
                    Step 4: 调用 <code>GetVisualValidateResult</code> 用
                    BytedToken 换取 GroupId
                  </Message.Item>
                  <Message.Item>
                    Step 5: 用 GroupId 调用 <code>CreateAsset</code>{' '}
                    上传素材（系统自动比对人脸）
                  </Message.Item>
                  <Message.Item>
                    Step 6: 轮询 <code>GetAsset</code> 等待素材状态变为{' '}
                    <code>Active</code>，即可用于视频生成
                  </Message.Item>
                </Message.List>
              </Message>

              {/* Step 1 */}
              <Header as='h4'>Step 1: 创建真人认证会话</Header>
              <p>
                生成 H5 活体认证链接。<code>callback_url</code>{' '}
                为认证完成后的跳转地址，<code>project_name</code> 默认为{' '}
                <code>default</code>。
              </p>
              <div
                style={codeStyle}
              >{`curl -X POST ${baseURL}/v1/visual_validate/session \\
  -H "Authorization: Bearer sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Doubao-Seedance-2.0",
    "callback_url": "${baseURL}/visual-validate/callback",
    "project_name": "default"
  }'`}</div>
              <p style={{ marginTop: 10 }}>响应示例：</p>
              <div style={codeStyle}>{`{
  "Result": {
    "BytedToken": "20260420162904CECD54BDAE...",
    "H5Link": "https://h5-v2.kych5.com?accessKeyId=...&bytedToken=...&lng=zh",
    "CallbackURL": "${baseURL}/visual-validate/callback"
  },
  "ResponseMetadata": {
    "RequestId": "...",
    "Action": "CreateVisualValidateSession",
    "Version": "2024-01-01",
    "Service": "ark",
    "Region": "cn-beijing"
  }
}`}</div>

              {/* Step 2 */}
              <Header as='h4' style={{ marginTop: 20 }}>
                Step 2: 用户完成活体认证
              </Header>
              <p>
                将 <code>H5Link</code>{' '}
                发送给终端用户，在手机浏览器中打开完成人脸认证。
                认证完成后，页面自动跳转到 CallbackURL 并拼接参数：
              </p>
              <div
                style={codeStyle}
              >{`${baseURL}/visual-validate/callback?bytedToken=20260420...&resultCode=10000&algorithmBaseRespCode=0&reqMeasureInfoValue=1&verify_type=real_time`}</div>
              <Message warning style={{ marginTop: 10 }}>
                <Message.List>
                  <Message.Item>
                    <code>resultCode=10000</code> 表示认证成功
                  </Message.Item>
                  <Message.Item>
                    H5Link 和 BytedToken 均 120 秒有效
                  </Message.Item>
                  <Message.Item>
                    认证失败需重新调用 Step 1 生成新链接
                  </Message.Item>
                </Message.List>
              </Message>

              {/* Step 3 */}
              <Header as='h4' style={{ marginTop: 20 }}>
                Step 3: 获取认证结果（GroupId）
              </Header>
              <p>
                认证成功后，用 <code>BytedToken</code> 查询本次认证创建的 Asset
                Group ID。
              </p>
              <div
                style={codeStyle}
              >{`curl -X POST ${baseURL}/v1/visual_validate/result \\
  -H "Authorization: Bearer sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Doubao-Seedance-2.0",
    "byted_token": "20260420162904CECD54BDAE...",
    "project_name": "default"
  }'`}</div>
              <p style={{ marginTop: 10 }}>响应示例：</p>
              <div style={codeStyle}>{`{
  "Result": {
    "GroupId": "group-20260420163000-xxxxx"
  }
}`}</div>

              {/* Step 4 */}
              <Header as='h4' style={{ marginTop: 20 }}>
                Step 4: 上传素材到认证人像组
              </Header>
              <p>
                用 GroupId
                上传图片/视频/音频素材。系统会自动将素材与认证人脸比对，
                非同一人无法入库。
              </p>
              <div style={codeStyle}>{`curl -X POST ${baseURL}/v1/assets \\
  -H "Authorization: Bearer sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Doubao-Seedance-2.0",
    "group_id": "group-20260420163000-xxxxx",
    "url": "https://example.com/photo.jpg",
    "asset_type": "Image",
    "project_name": "default"
  }'`}</div>
              <p style={{ marginTop: 10 }}>响应示例：</p>
              <div style={codeStyle}>{`{
  "Result": {
    "Id": "asset-20260420163100-xxxxx"
  }
}`}</div>

              {/* Step 5 */}
              <Header as='h4' style={{ marginTop: 20 }}>
                Step 5: 查询素材状态
              </Header>
              <p>
                素材上传后需要预处理，轮询 <code>GetAsset</code> 直到状态变为{' '}
                <code>Active</code>。
              </p>
              <div style={codeStyle}>{`curl -X POST ${baseURL}/v1/assets/get \\
  -H "Authorization: Bearer sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Doubao-Seedance-2.0",
    "id": "asset-20260420163100-xxxxx",
    "project_name": "default"
  }'`}</div>
              <p style={{ marginTop: 10 }}>
                当 <code>Status</code> 为 <code>Active</code> 后，即可使用{' '}
                <code>asset://asset-xxx</code> URI 进行视频生成。
              </p>

              {/* Step 6 */}
              <Header as='h4' style={{ marginTop: 20 }}>
                Step 6: 使用素材生成视频
              </Header>
              <p>
                在视频生成请求中，通过 <code>asset://&lt;asset_id&gt;</code>{' '}
                引用已入库的真人素材。
              </p>
              <div
                style={codeStyle}
              >{`curl -X POST ${baseURL}/v1/content_generation/tasks \\
  -H "Authorization: Bearer sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Doubao-Seedance-2.0",
    "content": [
      {
        "type": "text",
        "text": "图片1中的人物面带微笑，缓缓转头看向镜头"
      },
      {
        "type": "image_url",
        "image_url": { "url": "asset://asset-20260420163100-xxxxx" },
        "role": "reference_image"
      }
    ],
    "ratio": "16:9",
    "duration": 5
  }'`}</div>

              <Message style={{ marginTop: 20 }}>
                <Message.Header>注意事项</Message.Header>
                <Message.List>
                  <Message.Item>
                    每个 Asset Group
                    唯一绑定一个真人，上传的素材会自动与认证基准人脸比对
                  </Message.Item>
                  <Message.Item>
                    素材和推理接入点必须在同一个 Project 下
                  </Message.Item>
                  <Message.Item>
                    Prompt
                    中用「图片1」「视频1」指代素材，序号为请求体中同类素材的顺序，不要直接写
                    Asset ID
                  </Message.Item>
                  <Message.Item>素材检测到多个人脸时无法入库</Message.Item>
                </Message.List>
              </Message>
            </Accordion.Content>
          </Accordion>
        </Card.Content>
      </Card>
    </div>
  );
};

export default VisualValidate;
